import { useState } from 'react';
import { useGoogleLogin } from '@react-oauth/google';
import { useNavigate } from 'react-router-dom';
import { storeJSONData, deleteIndexedDbTable } from '@utility/indexedDB_utils';
const fileName = "freeweight_database.json";            // to public or on seperate drive to fetch
const initialJsonPath = "freeweight_database.json";    // public will still load and the default jsons should be pretty small, but will require to load every time the web
                                                        // app is accessed, but if a new user is signing up it will instantly have the data to start, I believe it most likely
                                                        // makes sense to have it stored on a seperate drive and fetch on a data wipe or signup but it will be decided later.

export const useGoogleAuth = () => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  const login = useGoogleLogin(
    {
    flow: 'auth-code',
    scope: 'https://www.googleapis.com/auth/drive.file https://www.googleapis.com/auth/drive.appdata',
    access_type: 'offline',
    prompt: 'consent',
    redirect_uri: window.location.origin + (window.location.hostname.includes('vercel.app') ? '/freeweight' : ''),
    onSuccess: async (codeResponse) => {
      console.log("Authorization Code:", codeResponse.code);
      setIsLoading(true);
      setError(null)
      console.log('Client ID:', process.env.REACT_APP_GOOGLE_CLIENT_ID);
      console.log('Client Secret exists:', !!process.env.REACT_APP_GOOGLE_CLIENT_SECRET);


      try {
        // Exchange the code for tokens
        const tokenResponse = await exchangeCodeForTokens(codeResponse.code);
        console.log("Token Response:", tokenResponse);
        
        // Store the tokens
        localStorage.setItem('accessToken', tokenResponse.access_token);
        if (tokenResponse.refresh_token) {
          console.log("Refresh Token Received:", tokenResponse.refresh_token);
          localStorage.setItem('refreshToken', tokenResponse.refresh_token);
        }

        const accessToken = tokenResponse.access_token;
        const fileExists = await doesFileExists(accessToken, fileName);

        if (!fileExists) {
          await uploadFile(accessToken, fileName, initialJsonPath);
          console.log("Json data uploaded to Google Drive.");
        }

        const jsonData = await downloadFile(accessToken, fileName);
        if (jsonData) {
          await storeJSONData(jsonData);
          console.log("Data stored into IndexedDB from Google Drive.");
        }
        
        await fetchUserProfile(accessToken);
        navigate('/home');
      } catch (error) {
        console.error("Error during authentication or file operations:", error);
      } finally {
        setIsLoading(false);
      }
    },
    onError: (error) => {
      setError(error.message);
      setIsLoading(false);
      console.log('Login Failed:', error);
    }
  });

  const exchangeCodeForTokens = async (code) => {
    const clientId = process.env.REACT_APP_GOOGLE_CLIENT_ID; // Add to vercel secret when deployed
    const redirectUri = window.location.origin; // should work for local or deployment
    
    try {
      const response = await fetch('https://oauth2.googleapis.com/token', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams({
          code: code,
          client_id: clientId, // Add to vercel secret when deployed
          client_secret: process.env.REACT_APP_GOOGLE_CLIENT_SECRET, // Add to vercel secret when deployed
          redirect_uri: redirectUri,
          grant_type: 'authorization_code',
        }),
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        console.error("Token exchange error:", errorData);
        throw new Error(errorData.error_description || 'Failed to exchange code for tokens');
      }
      
      return await response.json();
    } catch (error) {
      console.error("Error exchanging code for tokens:", error);
      throw error;
    }
  };

  const reauthenticate = (pendingOperation) => {
    // does operation after successful relogin/ reauthenticate
    if (pendingOperation) {
      localStorage.setItem('pendingOperation', pendingOperation);
    }
    
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    login();
  };

  return { login, reauthenticate, isLoading, error };
};

export const initializeGuestSession = async () => {
  try {
    // Load default data from public folder
    const response = await fetch(initialJsonPath);
    if (!response.ok) throw new Error("Failed to fetch default data");
    
    const jsonData = await response.json();
    await storeJSONData(jsonData); // Store in IndexedDB
    
    // Set guest flags
    localStorage.setItem('isGuest', 'true');
    localStorage.setItem('username', 'Guest User');
    localStorage.setItem('profilePicture', '');
    
    return true;
  } catch (error) {
    console.error("Guest initialization failed:", error);
    return false;
  }
};

export const fetchUserProfile = async (accessToken) => {
  try {
    const response = await fetch('https://www.googleapis.com/oauth2/v3/userinfo', {
      headers: { Authorization: `Bearer ${accessToken}` },
    });

    const userInfo = await response.json();
    localStorage.setItem('googleUser', JSON.stringify(userInfo));
    localStorage.setItem('profilePicture', userInfo.picture || '');
    localStorage.setItem('username', userInfo.given_name || userInfo.name || "");
  } catch (error) {
    console.error("Error fetching user profile:", error);
  }
};

export const doesFileExists = async (accessToken, fileName) => {
  const query = `name='${fileName.replace(/'/g, "\\'")}' and 'appDataFolder' in parents`;

  try {
    const searchResponse = await fetch(
      `https://www.googleapis.com/drive/v3/files?q=${encodeURIComponent(query)}&spaces=appDataFolder`, 
      { 
        headers: { 
          Authorization: `Bearer ${accessToken}`,
          Accept: 'application/json' 
        } 
      }
    ); 

    const data = await searchResponse.json();
    return data.files && data.files.length > 0;
  } catch (error) {
    console.error(`Error in doesFileExists("${fileName}"): ${error}`);
    return false;
  }
};

export const uploadFile = async (accessToken, fileName, fileData) => {
  try {
    let fileBlob;
    
    if (typeof fileData === 'string') {
      // Handle file path case
      const fileResponse = await fetch(`${process.env.PUBLIC_URL}/${fileData}`);
      if (!fileResponse.ok) throw new Error("Failed to fetch the file locally.");
      fileBlob = await fileResponse.blob();
    } else if (fileData instanceof Blob) {
      // Handle Blob case
      fileBlob = fileData;
    } else {
      throw new Error("Invalid file data type");
    }

    if (!fileName.endsWith('.json')) throw new Error("Invalid file type. Only .json files are allowed.");

    const metadata = {
      name: fileName,
      parents: ['appDataFolder'],
      mimeType: 'application/json',
    };

    const formData = new FormData();
    formData.append("metadata", new Blob([JSON.stringify(metadata)], { type: "application/json" }));
    formData.append("file", fileBlob);

    const uploadResponse = await fetch(
      'https://www.googleapis.com/upload/drive/v3/files?uploadType=multipart',
      {
        method: 'POST',
        headers: { Authorization: `Bearer ${accessToken}` },
        body: formData,
      }
    );

    return await uploadResponse.json();
  } catch (error) {
    console.error(`Error uploading ${fileName} file:`, error);
    throw error;
  }
};

export const getFileId = async (accessToken, fileName) => {
  const query = `name='${fileName}' and 'appDataFolder' in parents`;

  try {
    const searchResponse = await fetch(
      `https://www.googleapis.com/drive/v3/files?q=${encodeURIComponent(query)}&spaces=appDataFolder`,
      { headers: { Authorization: `Bearer ${accessToken}` } }
    );

    if (!searchResponse.ok) {
      throw new Error(await searchResponse.text());
    }

    const data = await searchResponse.json();
    return data.files?.[0]?.id || null;
  } catch (error) {
    console.error(`Error getting ${fileName}'s ID:`, error);
    return null;
  }
};

export const downloadFile = async (accessToken, fileName) => {
  const fileId = await getFileId(accessToken, fileName);
  if (!fileId) throw new Error(`File ${fileName} not found`);

  try {
    const response = await fetch(
      `https://www.googleapis.com/drive/v3/files/${fileId}?alt=media&spaces=appDataFolder`,
      { headers: { Authorization: `Bearer ${accessToken}` } }
    );

    if (!response.ok) throw new Error(await response.text());

    const blob = await response.blob();
    const blobText = await blob.text();
    return JSON.parse(blobText);
  } catch (error) {
    console.error("Error downloading DB file:", error);
    throw error;
  }
};

export async function updateFile(accessToken, fileId, fileBlob) {
  try {
    const updateResponse = await fetch(
      `https://www.googleapis.com/upload/drive/v3/files/${fileId}?uploadType=media`,
      {
        method: 'PATCH',
        headers: { 
          Authorization: `Bearer ${accessToken}`,
          'Content-Type': 'application/json'
        },
        body: fileBlob
      }
    );

    if (!updateResponse.ok) {
      const errorText = await updateResponse.text();
      throw new Error(`Failed to update file: ${errorText}`);
    }

    const result = await updateResponse.json();
    console.log("File updated successfully:", result);
    return result;
  } catch (error) {
    console.error("Error updating file:", error);
    throw error;
  }
}

export const createNewFile = async (accessToken, fileName, fileBlob) => {
  return executeWithAuthentication(async (token) => {
    try {
      // Metadata for Google Drive upload
      const metadata = {
        name: fileName,
        parents: ['appDataFolder'],
        mimeType: 'application/json',
      };

      // Prepare form data for upload
      const formData = new FormData();
      formData.append("metadata", new Blob([JSON.stringify(metadata)], { type: "application/json" }));
      formData.append("file", fileBlob);

      // Upload to Google Drive
      const uploadResponse = await fetch(
        'https://www.googleapis.com/upload/drive/v3/files?uploadType=multipart',
        {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}` },
          body: formData,
        }
      );

      if (!uploadResponse.ok) {
        const errorText = await uploadResponse.text();
        if (uploadResponse.status === 401) {
          throw new Error('AUTH_REQUIRED');
        }
        throw new Error(`Failed to create file: ${errorText}`);
      }

      const result = await uploadResponse.json();
      console.log("File created successfully:", result);
      return result;
    } catch (error) {
      console.error("Error creating file:", error);
      throw error;
    }
  });
};

export const isTokenValid = async (accessToken) => {
  if (!accessToken) return false;
  
  try {
    const response = await fetch('https://www.googleapis.com/oauth2/v3/tokeninfo', {
      headers: { Authorization: `Bearer ${accessToken}` },
    });
    
    if (!response.ok) return false;
    
    const tokenInfo = await response.json();
    const currentTime = Math.floor(Date.now() / 1000);
    return tokenInfo.exp && tokenInfo.exp > currentTime;
  } catch (error) {
    console.error("Error checking token validity:", error);
    return false;
  }
};
  
export const useReauthenticate = () => {
  const navigate = useNavigate();
  const { login } = useGoogleLogin({
    scope: 'https://www.googleapis.com/auth/drive.file https://www.googleapis.com/auth/drive.appdata',
    onSuccess: (tokenResponse) => {
      localStorage.setItem('accessToken', tokenResponse.access_token);
      if (tokenResponse.refresh_token) {
        localStorage.setItem('refreshToken', tokenResponse.refresh_token);
      }
      window.location.reload(); // Reload the page after successful authentication
    },
    onError: (error) => {
      console.error("Reauthentication failed:", error);
    }
  });

  const reauthenticate = async () => {
    try {
      // First try silent refresh
      const newToken = await refreshAccessToken();
      return newToken;
    } catch (error) {
      console.log('Silent refresh failed, initiating full login:', error);
      // Clear invalid tokens
      localStorage.removeItem('accessToken');
      localStorage.removeItem('refreshToken');
      
      // Start login flow
      login();
      // Return a promise that won't resolve (since we're redirecting)
      return new Promise(() => {});
    }
  };

  return reauthenticate;
};

export const authenticatedFetch = async (url, options = {}) => {
  let accessToken = localStorage.getItem('accessToken');
  
  // Check token validity before making the request
  if (accessToken && !(await isTokenValid(accessToken))) {
    try {
      accessToken = await refreshAccessToken();
      if (!accessToken) throw new Error('Token refresh failed');
    } catch (error) {
      // Refresh token is invalid or expired, need full reauth
      throw new Error('AUTH_REQUIRED');
    }
  }
  
  const response = await fetch(url, {
    ...options,
    headers: {
      ...options.headers,
      Authorization: `Bearer ${accessToken}`
    }
  });

  if (response.status === 401) {
    // Even with our validity check, we still got a 401
    // This indicates the token is definitely invalid
    throw new Error('AUTH_REQUIRED');
  }

  return response;
};

export const refreshAccessToken = async () => {
  // Get refresh token from localStorage
  const refreshToken = localStorage.getItem('refreshToken');
  
  // If there's no refresh token available, we can't refresh the access token
  if (!refreshToken) {
    console.error('No refresh token available for token refresh');
    throw new Error('No refresh token available');
  }
  
  try {
    const clientId = process.env.REACT_APP_GOOGLE_CLIENT_ID; // move to vercel secret when deployed
    
    // Make the token refresh request
    const response = await fetch('https://oauth2.googleapis.com/token', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      body: new URLSearchParams({
        client_id: clientId,
        refresh_token: refreshToken,
        grant_type: 'refresh_token',
      }),
    });

    // Check for successful response
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      console.error('Token refresh failed:', errorData);
      throw new Error('Token refresh failed');
    }

    // Parse the response data
    const data = await response.json();
    
    // Store the new access token
    if (data.access_token) {
      localStorage.setItem('accessToken', data.access_token);
      // If a new refresh token is provided, update it as well
      if (data.refresh_token) {
        localStorage.setItem('refreshToken', data.refresh_token);
      }
      
      return data.access_token;
    } else {
      throw new Error('No access token in refresh response');
    }
  } catch (error) {
    console.error('Error refreshing token:', error);
    throw error;
  }
};

export const executeWithAuthentication = async (operation) => {
  try {
    let accessToken = localStorage.getItem('accessToken');
    
    // Check token validity before operation
    if (!accessToken || !(await isTokenValid(accessToken))) {
      accessToken = await refreshAccessToken();
    }
    
    return await operation(accessToken);
  } catch (error) {
    if (error.message === 'AUTH_REQUIRED' || 
        (error.message && error.message.includes('Invalid Credentials'))) {
      throw new Error('AUTH_REQUIRED');
    }
    throw error;
  }
};

export const listFilesInAppDataFolder = async (accessToken) => {
  try {
    // Query to fetch all files from 'appDataFolder'
    const query = `'appDataFolder' in parents`;
    
    // Fetch the list of files from the appDataFolder
    const response = await fetch(
      `https://www.googleapis.com/drive/v3/files?q=${encodeURIComponent(query)}&spaces=appDataFolder`,
      {
        headers: { Authorization: `Bearer ${accessToken}` }
      }
    );
    
    // Check if the response is successful
    if (!response.ok) {
      throw new Error("Failed to fetch files from Google Drive.");
    }

    const data = await response.json();

    // If files are found
    if (data.files && data.files.length > 0) {
      console.log("Files in AppData Folder:");
      data.files.forEach((file) => {
        console.log(`File name: ${file.name}, File ID: ${file.id}`);
      });
    } else {
      console.log("No files found in the appDataFolder.");
    }
  } catch (error) {
    console.error("Error fetching files:", error);
  }
};

export const deleteAllFilesInAppDataFolder = async (accessToken) => {
  const query = `'appDataFolder' in parents`;

  try {
    // Fetch the list of files in the appDataFolder
    const listResponse = await fetch(
      `https://www.googleapis.com/drive/v3/files?q=${encodeURIComponent(query)}&spaces=appDataFolder`,
      {
        headers: { Authorization: `Bearer ${accessToken}` },
      }
    );

    const data = await listResponse.json();

    if (data.files && data.files.length > 0) {
      // Loop through each file and delete it
      for (const file of data.files) {
        const fileId = file.id;
        console.log(`Deleting file with ID: ${fileId}`);

        const deleteResponse = await fetch(
          `https://www.googleapis.com/drive/v3/files/${fileId}`,
          {
            method: 'DELETE',
            headers: { Authorization: `Bearer ${accessToken}` },
          }
        );

        if (deleteResponse.ok) {
          console.log(`✅ File with ID ${fileId} deleted successfully.`);
        } else {
          console.error(`❌ Failed to delete file with ID ${fileId}.`);
        }
      }
    } else {
      console.log("No files found in appDataFolder.");
    }
  } catch (error) {
    console.error("Error deleting files:", error);
  }
};

export const resetDatabase = async () => {
  const accessToken = localStorage.getItem('accessToken');
  const jsonUrl = 'https://freeweight.vercel.app/freeweight_database.json';

  if (!accessToken) throw new Error('No access token available. Please authenticate first.');

  try {
    // 1. Clear existing data
    await Promise.all([
      deleteIndexedDbTable().catch(err => console.warn("Error deleting IndexedDB:", err)),
      deleteAllFilesInAppDataFolder(accessToken).catch(err => console.warn("Error deleting Drive files:", err))
    ]);

    // 2. Fetch the default JSON data
    const response = await fetch(jsonUrl);
    
    if (!response.ok) throw new Error(`Failed to fetch JSON (HTTP ${response.status})`);
    
    // Verify content type is JSON
    const contentType = response.headers.get('content-type');
    if (!contentType || !contentType.includes('application/json')) {
      const text = await response.text();
      console.error('Received non-JSON response:', text.substring(0, 100));
      throw new Error('Server returned non-JSON response');
    }

    const jsonData = await response.json();

    // 3. Upload to Google Drive
    const fileBlob = new Blob([JSON.stringify(jsonData)], { type: 'application/json' });
    await uploadFile(accessToken, fileName, fileBlob);
    console.log("stored in google drive");
    // 4. Store in IndexedDB
    await storeJSONData(jsonData);
    console.log("stored indexdb");
    console.log("Database reset successful!");
    return true;
  } catch (error) {
    console.error("Database reset failed:", error);
    throw error;
  }
};